import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { MatwinsSkins } from '@app/common-codes/skins/global.skin';
import { IStaticStylesConfig } from '@app/common-codes/skins/_primitives/IStaticStylesConfig';
import { AlertModalService, ToastType } from '@app/common-codes/services/AlertModalService';
import { TranslationService } from '@app/common-codes/services/TranslationService';
import { TranslatePipe } from '@app/common-codes/components/directives/TranslatePipe';
import { OverridenLanguage, OverrideTranslation } from '@app/common-codes/models/matwins-manager/overrideTranslation.model';
import { DependencyInjectionProvider } from '@app/common-codes/services/DependecyInjection.provider';
import { DataService } from '@app/common-codes/services/DataService';
import { MatwinsVariablesService } from '@app/common-codes/services/matwinsVariables.service';
import { Client } from '@app/common-codes/models/matwins-manager/Client.model';
import { PaginatedResponse } from '@app/common-codes/_DTOs/IPaginaterResponse';
import { StringHelpers } from '@app/common-codes/helpers/StringHelpers';

interface OverrideLanguageInfo { id: number; key: string; }
interface OverridePosInfo { id: number; name: string; }
interface OverridesContext { languages: OverrideLanguageInfo[]; pointOfServices: OverridePosInfo[]; }

interface OverrideRow {
  id: string;
  translationKey: string;
  // Une cellule par langue, dans l'ordre des colonnes.
  cells: OverridenLanguage[];
  searchText: string;
}

/**
 * Overrides de traductions d'un client (sans POS) ou d'un POS.
 * Toutes les traductions sont dans un seul defilement virtuel : seules les lignes visibles sont dans le DOM.
 */
@Component({
  selector: 'app-language-list',
  templateUrl: './language-list.component.html',
  styleUrls: ['./language-list.component.css'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, ScrollingModule, TranslatePipe]
})
export class LanguageListComponent implements OnInit {
  readonly Skin = MatwinsSkins.V1 as IStaticStylesConfig;
  readonly rowHeight = 64;

  private http = inject(HttpClient);
  private varsService = inject(MatwinsVariablesService);
  private alertModalService = inject(AlertModalService);
  private translationService = inject(TranslationService);
  private clientService: DataService<Client> = inject(DependencyInjectionProvider).getOrCreateDataService(Client);

  clients = signal<Client[]>([]);
  currentClient = signal<Client | null>(null);
  pointOfServices = signal<OverridePosInfo[]>([]);
  currentPosId = signal<number | null>(null);
  languages = signal<OverrideLanguageInfo[]>([]);

  rows = signal<OverrideRow[]>([]);
  searchTerm = signal('');
  loading = signal(false);
  saving = signal(false);
  dirty = signal(false);

  filteredRows = computed(() => {
    // Plusieurs mots : chacun doit se trouver dans la ligne (ET), mais pas forcement dans le meme champ.
    const words = StringHelpers.NormalizeForSearch(this.searchTerm()).split(/\s+/).filter(w => w.length > 0);
    const rows = this.rows();
    return words.length ? rows.filter(r => words.every(w => r.searchText.includes(w))) : rows;
  });

  gridTemplate = computed(() => `16rem repeat(${this.languages().length}, minmax(0, 1fr))`);

  async ngOnInit() {
    const params = { page: 1, size: null, search: null, sortBy: null, sortOrder: 'asc', searchColumns: null };
    const response = await this.clientService.execute<PaginatedResponse<Client>>('Post', 'role', params);
    this.clients.set(response?.items ?? []);
  }

  async onClientSelected(client: Client | null) {
    this.currentClient.set(client);
    this.currentPosId.set(null);
    this.pointOfServices.set([]);
    this.rows.set([]);
    if (!client) return;

    const context = await firstValueFrom(this.http.get<OverridesContext>(`${this.baseUrl}/overrides/context`, { headers: this.headers }));
    this.languages.set(context.languages);
    this.pointOfServices.set(context.pointOfServices);
    await this.loadOverrides();
  }

  async onPointOfServiceSelected(posId: number | null) {
    this.currentPosId.set(posId);
    await this.loadOverrides();
  }

  makeEditable(cell: OverridenLanguage) {
    if (cell.isOverrided) return;
    cell.isOverrided = true;
    this.dirty.set(true);
  }

  removeOverride(row: OverrideRow, cell: OverridenLanguage) {
    cell.isOverrided = false;
    cell.translationValue = cell.originalValue;
    this.onValueChanged(row);
  }

  onValueChanged(row: OverrideRow) {
    row.searchText = this.buildSearchText(row.id, row.cells);
    this.dirty.set(true);
  }

  async saveTranslations() {
    // On envoie la liste complete des overrides : le serveur reecrit les fichiers a partir d'elle.
    // Un override identique a la valeur heritee n'apporte rien : on ne le conserve pas.
    const overrides = this.rows()
      .map(row => ({
        id: row.id,
        translationKey: row.translationKey,
        overridenLanguages: row.cells.filter(c => c.isOverrided && c.translationValue !== c.originalValue)
      }))
      .filter(o => o.overridenLanguages.length > 0);

    const posId = this.currentPosId();
    const url = posId ? `${this.baseUrl}/SaveOverride?posId=${posId}` : `${this.baseUrl}/SaveOverride`;
    this.saving.set(true);
    try {
      await firstValueFrom(this.http.post(url, overrides, { headers: this.headers }));
      this.alertModalService.showToast(this.translationService.instant('messages.success'), ToastType.Success, 3000);
      await this.loadOverrides();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des overrides :', error);
      this.alertModalService.showToast(this.translationService.instant('messages.error'), ToastType.Error, 5000);
    } finally {
      this.saving.set(false);
    }
  }

  trackRow = (_: number, row: OverrideRow) => row.id;

  private async loadOverrides() {
    const posId = this.currentPosId();
    const url = posId ? `${this.baseUrl}/overrides?posId=${posId}` : `${this.baseUrl}/overrides`;
    this.loading.set(true);
    try {
      const items = await firstValueFrom(this.http.get<OverrideTranslation[]>(url, { headers: this.headers }));
      this.rows.set(items.map(item => this.toRow(item)));
      this.dirty.set(false);
    } catch (error) {
      console.error('Erreur lors du chargement des overrides :', error);
      this.alertModalService.showToast(this.translationService.instant('messages.error'), ToastType.Error, 5000);
    } finally {
      this.loading.set(false);
    }
  }

  private toRow(item: OverrideTranslation): OverrideRow {
    const byLanguage = new Map(item.overridenLanguages.map(l => [l.languageId, l]));
    const cells = this.languages().map(lang => byLanguage.get(lang.id)
      ?? { languageId: lang.id, translationValue: '', originalValue: '', isOverrided: false });
    return { id: item.id, translationKey: item.translationKey, cells, searchText: this.buildSearchText(item.id, cells) };
  }

  private buildSearchText(id: string, cells: OverridenLanguage[]): string {
    return StringHelpers.NormalizeForSearch([id, ...cells.map(c => c.translationValue)].join('\u0001'));
  }

  private get baseUrl(): string {
    return `${environment.apiBaseUrl}/api/translations`;
  }

  // Le client choisi ici prime sur le client courant de l'application : l'en-tete ClientId designe sa base.
  private get headers(): HttpHeaders {
    const client = this.currentClient();
    let headers = new HttpHeaders({
      'X-Custom-Matwins-BrowserId': this.varsService.browserId || '',
      'X-Custom-Matwins-UseAuthentication': this.varsService.useAuthentication.toString(),
      'X-Custom-Matwins-ClientId': client?.id ?? '',
      'X-Custom-Matwins-TenantPrefix': client?.url?.replace('https://', '').split('.')[0] ?? ''
    });
    const accessToken = this.varsService.getAccessToken();
    if (accessToken) headers = headers.set('Authorization', `Bearer ${accessToken}`);
    return headers;
  }
}
